import React, { memo } from 'react';
import { BusinessCard, BusinessCardProps } from './BusinessCard';
import { useSelectedBusiness, useBusinessActivities } from '@/stores';
import type { Business } from '@/types/homepage';

/**
 * BusinessCardContainer - Phase 2 Optimization Component
 * 
 * This container component uses domain stores to provide optimized data
 * to the existing BusinessCard component. It acts as a bridge between
 * the new selective subscription pattern and the existing UI component.
 * 
 * Benefits:
 * - Only re-renders when business-specific data changes
 * - Isolated from map state, search state, and UI state changes
 * - Maintains all existing BusinessCard functionality and UI
 * - Easy rollback by reverting to direct BusinessCard usage
 */

interface BusinessCardContainerProps {
  business: Business;
  onSelect?: (business: Business) => void;
}

// Custom comparison to prevent unnecessary re-renders
function areContainerPropsEqual(
  prevProps: BusinessCardContainerProps, 
  nextProps: BusinessCardContainerProps
) {
  // Only re-render if business ID changes
  return prevProps.business._id === nextProps.business._id;
}

export const BusinessCardContainer = memo(function BusinessCardContainer({ 
  business, 
  onSelect 
}: BusinessCardContainerProps) {
  
  // PHASE 2: Use domain stores for selective subscriptions
  const selectedBusiness = useSelectedBusiness();
  const businessActivities = useBusinessActivities();
  
  // Derive props for BusinessCard
  const businessCardProps: BusinessCardProps = {
    business,
    isSelected: selectedBusiness?._id === business._id,
    onSelect,
    activities: businessActivities[business._id] || []
  };
  
  // ESSENTIAL Debug logging for Phase 2 monitoring - REDUCED NOISE
  // Render logs commented out to reduce noise - keeping only mount/unmount tracking
  // if (process.env.NODE_ENV === 'development') {
  //   console.warn('🧪 [BusinessCardContainer] PHASE 2 RENDER:', {
  //     businessId: business._id,
  //     businessName: business.name,
  //     isSelected: selectedBusiness?._id === business._id,
  //     activitiesCount: (businessActivities[business._id]?.length || 0),
  //     phase2Active: true,
  //     timestamp: new Date().toISOString()
  //   });
  // }
  
  // Render the existing BusinessCard with optimized data
  return <BusinessCard {...businessCardProps} />;
  
}, areContainerPropsEqual);

export default BusinessCardContainer; 